import clsx from "clsx";
import React from "react";
import {
  Box,
  BoxProps,
  Typography,
  TextField,
  Button,
  useTheme,
  Collapse,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

import InputPlusMinus from "../input-plus-minus/InputPlusMinus";
import CoachSets from "../coach-sets/CoachSets";
import {
  ExerciseTypeExtra,
  ExerciseTypeReps,
  ExerciseTypeSet,
  UnitsTime,
  UserRole,
} from "../../constants";

import {
  DefaultWorkoutExercise,
  WorkoutExercise,
  WorkoutExerciseImage,
} from "../workout/types";
import { WorkoutMedia } from "../workout/WorkoutMedia";
import { WorkoutUploadMedia } from "../workout/WorkoutUploadMedia";
import {
  MAX_EXERCISE_SETS_COUNT,
  createEmptySet,
  defaultExerciseWithTitle,
  getSetFieldMaxLength,
  normalizeObsoleteExercise,
} from "../workout/utils";
import { WorkoutDemoVideo } from "../workout/WorkoutDemoVideo";
import { useCurrentUser, useCurrentUserRole } from "../../hooks/useCurrentUser";
import { WorkoutActionsDelete } from "../workout/WorkoutActionsDelete";
import WorkoutExerciseSettings from "../workout/WorkoutExerciseSettings";

import Settings from "../../icons/Settings";

import {
  AdminExerciseAssetDto,
  BodypartType,
  ExerciseAssetDto,
  ExerciseAssetStatus,
  ExerciseAssetType,
  ExerciseTagDto,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";

import { useApprovePendingExerciseLibraryAsset } from "./hooks/useApprovePendingExerciseLibraryAsset";
import { ExerciseLibraryAutocomplete } from "./ExerciseLibraryAutocomplete";
import CategorySelect from "./CategorySelect";
import ExerciseTagSelect from "./tags/ExerciseTagSelect";
import { useAdminSaveExerciseLibraryAsset } from "../admin/hooks/useAdminSaveExerciseLibraryAsset";
import { AddedLozenge } from "../item/AddedLozenge";
import { generateId } from "../new-editor/utils/nodeUtil";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  onUnArchivedSuccess,
  SELECTED_EXERCISE,
  useForcedRestoreLibraryExerciseMutation,
} from "./hooks/useArchiveRestoreLibraryExercise";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";
import { areExerciseValuesToApply } from "../workout/ManageWorkoutExercise";
import DefaultSettingsCallout, {
  CalloutStatus,
} from "./DefaultSettingsCallout";
import { useDebounce } from "../../hooks/useDebounce";
import { useSaveExerciseLibraryAsset } from "./hooks/useSaveExerciseLibraryAsset";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import { calloutColor } from "../new-editor/elements/Callout";
import { calloutSettingsStyles } from "./CalloutContainer";
import PendingAvatarWithTooltip from "./PendingAvatarWithTooltip";
import { useValidateBodyparts } from "./hooks/useValidateBodypart";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-between",
  },

  wrapper: {
    paddingBottom: theme.spacing(10),
  },

  workoutEditTitle: {
    color: theme.palette.secondary.main,
    textTransform: "capitalize",
    margin: `${theme.spacing(2.5, 0, 4, 0)}!important`,
  },

  setsContainerInput: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2.5, 0),
  },

  setsTopContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  settingsButton: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: 16,
  },

  label: {
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    fontSize: 16,
    margin: theme.spacing(2.5, 0, 2, 0),
  },

  videoLabel: {
    margin: theme.spacing(2, 0),
  },

  categoryTagLabel: {
    textTransform: "uppercase",
  },

  setsLabel: {
    marginRight: 10,
  },

  textField: {},

  input: {
    fontWeight: 500,
  },

  caretDownIcon: {
    width: 11,
  },

  images: {
    display: "flex",
    flexWrap: "wrap",
  },

  actions: {
    display: "flex",
    position: "sticky",
    zIndex: 999,
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 0),
    bottom: theme.spacing(-3),
    right: 0,
    left: 0,
  },
  manageButton: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: 14,
    padding: theme.spacing(0.5, 1),
    lineHeight: theme.spacing(2),
  },

  button: {
    borderWidth: "2px !important",
    borderStyle: "solid",
    padding: theme.spacing(1.25, 0),
    fontWeight: 700,
    fontSize: 14,
    "&:not(:last-child)": {
      marginRight: theme.spacing(2),
    },

    "&:not([disabled])": {
      borderColor: theme.palette.secondary.main,
    },

    "&[disabled]": {
      borderColor: theme.palette.text.secondary,
    },
  },

  cancelButton: {
    "&[disabled]": {
      color: theme.palette.text.secondary,
    },
  },

  saveButton: {
    "&[disabled]": {
      backgroundColor: theme.palette.text.secondary,
      color: theme.palette.common.white,
    },
  },

  checkboxLabel: {
    color: theme.palette.text.primary,
    fontWeight: "500 !important" as any,
  },
  addToLibraryText: {
    color: theme.palette.text.secondary,
    lineHeight: "17px",
    marginTop: 20,
  },
  upsertExerciseLibraryText: {
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  defaultsDescription: {
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  customizeButton: {
    fontWeight: 500,
    fontSize: 14,
    padding: theme.spacing(0.5, 1),
    lineHeight: theme.spacing(2),
  },
  setsContainer: {
    marginBottom: theme.spacing(3),
  },
  pendingNoInput: {
    "& .MuiAutocomplete-input, & .MuiAutocomplete-endAdornment": {
      display: "none",
    },
  },
}));

export type CategoryType = BodypartType | MovementPatternType;

export enum CategoryTypeLabel {
  Bodypart = "Bodypart",
  MovementPattern = "Movement",
}

export type ManageLibraryExerciseType =
  | ExerciseAssetDto
  | AdminExerciseAssetDto;
export interface ManageLibraryExerciseProps<T> extends BoxProps {
  exerciseAsset?: T;
  exerciseToSyncWith?: T;
  onUpdate?: (exercise: T) => void;
  onClose?: (event: any) => void;
  onExistedChange?: (existed: boolean) => void;
  onComplete?: (exercise: WorkoutExercise) => void;
  createNewAsset?: boolean;
  editingType: ExerciseEditingType;
  onSelectExercise?: (exercise: T, exerciseToSyncWith?: T) => void;
  onCreateExercise?: (exercise: WorkoutExercise) => void;
  workoutExerciseId?: number;
  showHeader?: boolean;
  setAssetId?: (id: number) => void;
  enableReadOnlyViewForPublic?: boolean;
  defaultComponentExerciseValues?: DefaultWorkoutExercise;
  disableChangeExercise?: boolean;
  allowChangeExerciseDuringRename?: boolean;
  isClient?: boolean;
}

export enum ExerciseEditingType {
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE",
  ALL = "ALL",
}

export const defaultValueContent = (exercise: ManageLibraryExerciseType) => {
  return exercise && exercise.content
    ? (JSON.parse(exercise.content) as WorkoutExercise)
    : (defaultExerciseWithTitle(exercise?.name || "") as WorkoutExercise);
};

export const normalizeExercise = (exercise: WorkoutExercise) => {
  return normalizeObsoleteExercise({
    ...(exercise ? exercise : {}),
    title: exercise?.title,
    subtitle: exercise?.subtitle,
    instructions: exercise?.instructions,
    images: exercise?.images,
    video: exercise?.video,
  });
};

const ALLOW_EMPTY_BODYPARTS = false;

export function ManageLibraryExercise<T extends ManageLibraryExerciseType>(
  props: ManageLibraryExerciseProps<T>,
) {
  const {
    className,
    onSelectExercise,
    exerciseAsset,
    exerciseToSyncWith,
    onUpdate,
    onClose,
    onExistedChange,
    setAssetId,
    createNewAsset,
    editingType,
    onComplete,
    workoutExerciseId,
    enableReadOnlyViewForPublic = false,
    defaultComponentExerciseValues,
    disableChangeExercise = false,
    allowChangeExerciseDuringRename = false,
    isClient = false,
    ...other
  } = props;
  const s = useStyles();
  const calloutStyles = calloutSettingsStyles({
    mainColor: calloutColor.info.main,
    backgroundColor: calloutColor.info.background,
  });

  const user = useCurrentUser();

  const isCurrentUserExerciseAsset = exerciseAsset?.coachId === user.id;

  const [renameMode, setRenameMode] = React.useState(false);

  const [defaultSettingExerciseMode, setDefaultSettingExerciseMode] =
    React.useState(CalloutStatus.SYNC);

  const [privateArchivedCopyMode, setPrivateArchivedCopyMode] = React.useState(
    CalloutStatus.SYNC,
  );
  // sync mode in case of selecting public or team exercise in the exercise library with existed private exercise with same name
  const [publicTeamCopySyncMode, setPublicTeamCopySyncMode] = React.useState(
    CalloutStatus.SYNC,
  );

  const viewPrivateAsAdmin =
    editingType === ExerciseEditingType.PUBLIC &&
    !!exerciseAsset?.coachId &&
    user.admin;

  const viewPublicOrTeamAsCoach =
    editingType === ExerciseEditingType.ALL &&
    !isCurrentUserExerciseAsset &&
    enableReadOnlyViewForPublic &&
    !createNewAsset &&
    user.role === UserRole.COACH;

  const [readOnly, setReadOnly] = React.useState(
    viewPrivateAsAdmin || viewPublicOrTeamAsCoach || isClient,
  );

  const defaultValue = defaultValueContent(exerciseAsset);

  const defaultNormalizedExercise = normalizeExercise(defaultValue);

  const [exercise, setExercise] = React.useState(defaultNormalizedExercise);
  const [dirty, setDirty] = React.useState(false);

  const [renamedTitle, setRenamedTitle] = React.useState<string>(
    exercise.title,
  );

  const delayedQuery = useDebounce(
    renameMode ? renamedTitle.trim() : exercise?.title?.trim(),
    250,
  );

  const {
    data: matchedLibraryExercises,
    refetch: refetchExerciseAssets,
    isFetching,
  } = useQuery({
    queryKey: [SELECTED_EXERCISE, { name: delayedQuery }],
    queryFn: ({ pageParam = 1 }) =>
      ExerciseAssetsService.getExerciseAssets(
        pageParam as number,
        2,
        renameMode ? renamedTitle : exercise?.title?.trim(),
        ExerciseAssetStatus.SINGLE_ACTIVE_AND_ARCHIVED,
        null,
        null,
        null,
        editingType === ExerciseEditingType.PUBLIC
          ? ExerciseAssetType.PUBLIC
          : ExerciseAssetType.PRIVATE,
        null,
        true,
      ),
    enabled: false,
  });

  const queryClient = useQueryClient();
  const validateBodyparts = useValidateBodyparts({
    showToastAlertFlag: false,
  });

  const assetNormalizedName = exerciseAsset?.name?.trim().toLocaleLowerCase();
  const exerciseNormalizedTitle = exercise?.title?.trim().toLocaleLowerCase();

  const isArchivedExercise =
    exerciseAsset?.archived && assetNormalizedName === exerciseNormalizedTitle;

  const archivedCopy = matchedLibraryExercises?.items?.find(
    (e) =>
      e.archived && e.name.trim().toLocaleLowerCase() === assetNormalizedName,
  );
  const activeExerciseAsset = matchedLibraryExercises?.items?.find(
    (e) =>
      !e.archived && e.name.trim().toLocaleLowerCase() === assetNormalizedName,
  );
  const saveWithUnArchivedCopy =
    archivedCopy && activeExerciseAsset?.coachId !== user.id;
  const matchedLibraryExercisesItem = activeExerciseAsset ?? archivedCopy;

  const { mutate: restore } = useForcedRestoreLibraryExerciseMutation(
    isArchivedExercise
      ? ExerciseAssetStatus.ARCHIVED
      : ExerciseAssetStatus.ACTIVE,
    true,
    !isArchivedExercise,
    archivedCopy,
  );

  const [selectedTags, setSelectedTags] = React.useState<
    ExerciseTagDto[] | null
  >(exerciseAsset?.tags);

  const { saveLibraryExercise: savePrivateExercise, isPending } =
    useSaveExerciseLibraryAsset(
      (createNewAsset || !isCurrentUserExerciseAsset) &&
        !saveWithUnArchivedCopy,
      selectedTags,
      ALLOW_EMPTY_BODYPARTS,
      editingType === ExerciseEditingType.ALL &&
        !isCurrentUserExerciseAsset &&
        "Library exercise updated.",
      isArchivedExercise && "Exercise restored.",
    );

  const { approveExercise } = useApprovePendingExerciseLibraryAsset(
    selectedTags,
    exerciseAsset?.notification,
  );

  const savePublicExercise = useAdminSaveExerciseLibraryAsset(createNewAsset);

  const [disableSaveButton, setDisableSaveButton] = React.useState(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = React.useState(false);
  const [chooseMedia, setChooseMedia] = React.useState(null);
  const [openExerciseSettings, setOpenExerciseSettings] = React.useState(false);

  const [addMedia, setAddMedia] = React.useState<
    WorkoutExerciseImage[] | WorkoutExerciseImage
  >(null);
  const [addOwnVideo, setAddOwnVideo] = React.useState(null);
  const [isAddingVideoOrMedia, setIsAddingVideoOrMedia] = React.useState(false);
  const typeReps = exercise?.typeReps || ExerciseTypeReps.WHOLE;
  const typeSet = exercise?.typeSet || ExerciseTypeSet.WEIGHT;
  const [selectedBodypart, setSelectedBodypart] = React.useState<
    BodypartType[] | null
  >(exerciseAsset?.bodyparts);
  const [selectedMovementPatternType, setSelectedMovementPatternType] =
    React.useState<MovementPatternType[] | null>(
      exerciseAsset?.movementPatterns,
    );

  const bodypartsBoxRef = React.useRef<HTMLDivElement>(null);
  const [highlightEmptyBodyparts, setHighlightEmptyBodyparts] =
    React.useState(false);

  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const [existedExerciseWithSameName, setExistedExerciseWithSameName] =
    React.useState<T>(null);

  const handleRenamedTitleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { value } = event.currentTarget;
      setRenamedTitle(value);
      if (errorMessage && errorMessage.trim().length > 0) {
        setErrorMessage("");
      }
    },
    [errorMessage],
  );

  const getDefaultUnits = (type: ExerciseTypeSet) =>
    type === ExerciseTypeSet.TIME ? UnitsTime.MINUTES : user.units;

  const handleDisableSaveButton = React.useCallback((value: boolean) => {
    setDisableSaveButton(value);
  }, []);

  const handleOpenModal = React.useCallback((media) => {
    setVisibleDeleteModal(true);
    setChooseMedia(media);
  }, []);

  const handleExerciseFullInfoSync = React.useCallback(
    (exerciseInfo: T, syncTags: boolean = true) => {
      setSelectedMovementPatternType(exerciseInfo?.movementPatterns);
      setSelectedBodypart(exerciseInfo?.bodyparts);
      syncTags && setSelectedTags(exerciseInfo?.tags);
      setExercise(
        normalizeExercise(defaultValueContent(exerciseInfo) as WorkoutExercise),
      );
    },
    [setAssetId, exerciseAsset, onUpdate],
  );

  const handleClickSettings = React.useCallback(() => {
    setOpenExerciseSettings((value) => !value);
  }, [setOpenExerciseSettings]);

  const handleCloseModal = React.useCallback(() => {
    setVisibleDeleteModal(false);
    setChooseMedia(null);
  }, []);

  const handleRemoveMedia = React.useCallback(
    (image: WorkoutExerciseImage) => {
      const images = exercise.images.filter((it) => it !== image);
      setExercise({
        ...exercise,
        images,
      });
      setDirty(true);
    },
    [exercise],
  );

  const handleRemoveDemoVideo = React.useCallback(() => {
    setExercise({
      ...exercise,
      video: null,
    });
    setDirty(true);
  }, [exercise]);

  const confirmAction = React.useCallback(() => {
    if (!chooseMedia?.isOwnVideo) {
      handleRemoveMedia(chooseMedia?.media);
      setVisibleDeleteModal(false);
    } else {
      handleRemoveDemoVideo();
      setVisibleDeleteModal(false);
    }
  }, [
    chooseMedia,
    handleRemoveMedia,
    setVisibleDeleteModal,
    handleRemoveDemoVideo,
  ]);

  const handleToggleCoverMedia = React.useCallback(
    (image: WorkoutExerciseImage) => {
      const images = exercise.images.map((it) => ({
        ...it,
        cover: !it.cover && it === image,
      }));

      setExercise({
        ...exercise,
        images,
      });
      setDirty(true);
    },
    [exercise],
  );

  const handleAutocompleteTitleChange = React.useCallback(
    (_, title: string) => {
      setExercise({
        ...exercise,
        title,
      });
      setDirty(true);
    },
    [exercise],
  );

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { name, value } = event.currentTarget;
      setExercise({
        ...exercise,
        [name]: value,
      });
      setDirty(true);
      setDefaultSettingExerciseMode(null);
    },
    [exercise],
  );

  const handleSetFieldChange = React.useCallback(
    (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      index: number,
    ) => {
      // eslint-disable-next-line prefer-const
      let { name, value } = event.currentTarget;

      const maxLength = getSetFieldMaxLength(name, typeReps, typeSet);
      if (value.replace(".", "").length <= maxLength || value === "") {
        const sets = [...exercise.sets];
        sets[index] = {
          ...sets[index],
          [name]: value,
        };
        setExercise({
          ...exercise,
          sets: [...sets],
        });
        setDirty(true);
        setDefaultSettingExerciseMode(null);
      }
    },
    [exercise],
  );

  const handleExerciseSettingsChange = React.useCallback(
    (field, value) => {
      if (field === "units") {
        setExercise({
          ...exercise,
          units: value,
        });
      } else if (field === "typeExtraMeasurement") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            extraMeasurement: "",
          })),
        });
      } else if (field === "typeReps") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            reps: "",
          })),
        });
      } else if (field === "typeSet") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            weight: "",
          })),
          units: getDefaultUnits(value),
        });
      } else {
        setExercise({
          ...exercise,
          [field]: value,
        });
      }
      setDirty(true);
      setDefaultSettingExerciseMode(null);
    },
    [setExercise, exercise, user.units],
  );

  const handleSetsNumberPlus = React.useCallback(() => {
    if (exercise.sets.length < MAX_EXERCISE_SETS_COUNT) {
      setDirty(true);
      setExercise({
        ...exercise,
        sets: [...exercise.sets, createEmptySet()],
      });
      setDefaultSettingExerciseMode(null);
    }
  }, [exercise]);

  const handleSetsNumberMinus = React.useCallback(() => {
    if (exercise.sets.length > 1) {
      setDirty(true);
      const sets = [...exercise.sets];
      sets.pop();
      setExercise({
        ...exercise,
        sets: [...sets],
      });
      setDefaultSettingExerciseMode(null);
    }
  }, [exercise]);

  const handleDiscard = React.useCallback(() => {
    setDirty(false);
    setExercise(defaultNormalizedExercise);
    setSelectedBodypart(exerciseAsset?.bodyparts);
    setSelectedMovementPatternType(exerciseAsset?.movementPatterns);
    setSelectedTags(exerciseAsset?.tags);
  }, []);

  const handleClose = React.useCallback(
    (event: any) => {
      if (onClose) {
        onClose(event);
      }
    },
    [onClose],
  );

  const handleUnArchiveSuccess = React.useCallback(() => {
    onUnArchivedSuccess(queryClient, isArchivedExercise);
  }, [isArchivedExercise]);

  const scrollToBodyparts = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSave = React.useCallback(
    (event) => {
      const defaultExerciseConfig = {
        typeSet: ExerciseTypeSet.WEIGHT,
        typeReps: ExerciseTypeReps.WHOLE,
        units: getDefaultUnits(exercise.typeSet),
      };

      // Remove assetId from exercise to keep exercise asset content consistent
      // For saving content to the component exercise, assetId is added in onComplete callback
      const { assetId, ...exerciseWithoutAssetId } = exercise ?? {};

      const exerciseData = {
        id: isCurrentUserExerciseAsset ? exerciseAsset.id : null,
        exercise: {
          ...defaultExerciseConfig,
          ...exerciseWithoutAssetId,
          id: workoutExerciseId || exercise.id,
        } as WorkoutExercise,
        name: exercise.title,
        bodyparts: selectedBodypart,
        movementPatterns: selectedMovementPatternType,
      };

      const isValidBodyparts = validateBodyparts(selectedBodypart);
      if (!isValidBodyparts) {
        setHighlightEmptyBodyparts(true);
        scrollToBodyparts(bodypartsBoxRef);
        return;
      }

      //Omitting saving to the database if the exercise is not changed
      if (
        !dirty &&
        (isCurrentUserExerciseAsset ||
          editingType === ExerciseEditingType.PRIVATE) &&
        !saveWithUnArchivedCopy &&
        !exerciseAsset?.isPendingApproval
      ) {
        onComplete?.({
          ...exerciseData.exercise,
          assetId: exerciseData.id,
        });
        handleClose(event);
        setDisableSaveButton(false);
        return;
      }

      if (
        editingType === ExerciseEditingType.PRIVATE ||
        editingType === ExerciseEditingType.ALL ||
        saveWithUnArchivedCopy
      ) {
        setDisableSaveButton(true);
        if (exerciseAsset?.isPendingApproval) {
          approveExercise(
            {
              ...exerciseData,
              tagIds: selectedTags?.flatMap((tag) => tag.id),
            },
            {
              onCompleted: () => {
                if (onComplete) {
                  onComplete({
                    ...exerciseData.exercise,
                    assetId: exerciseAsset.id,
                  });
                }
                handleClose(event);
                setDisableSaveButton(false);
              },
            },
          );
          return;
        }
        const id = saveWithUnArchivedCopy ? archivedCopy.id : exerciseData.id;
        const saveExercise = (processUnarchive?: () => void) =>
          savePrivateExercise(
            {
              ...exerciseData,
              tagIds: selectedTags?.flatMap((tag) => tag.id),
              id: id,
            },
            {
              onCompleted: (exerciseId) => {
                if (onComplete) {
                  onComplete({
                    ...exerciseData.exercise,
                    assetId: id || exerciseId,
                  });
                }
                if (processUnarchive) {
                  processUnarchive();
                }
                handleClose(event);
                setDisableSaveButton(false);
              },
            },
          );

        if (saveWithUnArchivedCopy)
          restore(
            {
              id: archivedCopy.id,
            },
            {
              onSuccess: () => {
                saveExercise(handleUnArchiveSuccess);
              },
            },
          );
        else {
          saveExercise();
        }
      } else {
        savePublicExercise(
          {
            ...exerciseData,
          },
          {
            onCompleted: () => {
              handleClose(event);
              setDisableSaveButton(false);
            },
          },
        );
      }
    },
    [
      exercise,
      onUpdate,
      savePrivateExercise,
      savePublicExercise,
      editingType,
      onUnArchivedSuccess,
    ],
  );

  const handleUpload = React.useCallback(
    (image: WorkoutExerciseImage[] | WorkoutExerciseImage) => {
      if (Array.isArray(image)) {
        const images = [...exercise.images];
        image.map((item, index) =>
          images.push({
            ...item,
            cover: !exercise.images.length && !index,
          }),
        );
        setExercise({
          ...exercise,
          images: [...images],
        });
      } else {
        const images = [
          ...exercise.images,
          {
            ...image,
            cover: !exercise.images.length,
          },
        ];

        setExercise({
          ...exercise,
          images,
        });
      }
      setDirty(true);
    },
    [exercise],
  );

  const handleUploadVideo = React.useCallback(
    (video: { url: string; name: string }) => {
      setExercise({
        ...exercise,
        video: video.url,
      });
      setDirty(true);
    },
    [exercise],
  );

  const inputProps = {
    id: "library-workout-exercise-autocomplete",
    name: "title",
    placeholder: !exerciseAsset && "ex. Alternating bodyweight lunge",
    fullWidth: true,
  };
  const theme = useTheme();
  React.useEffect(() => {
    if (addMedia) {
      handleUpload(addMedia);
      setAddMedia(null);
    }
    if (addOwnVideo) {
      handleUploadVideo(addOwnVideo);
      setAddOwnVideo(null);
    }
  }, [addMedia, handleUpload, addOwnVideo, handleUploadVideo]);

  const handleCloseAutocomplete = React.useCallback(
    (options: T[]) => {
      const matchedOption = options.find(
        (option) => option.name.trim() === exercise.title.trim(),
      );

      if (matchedOption) {
        onSelectExercise?.(matchedOption);
      }
    },
    [exercise.title],
  );

  const handleAutocompleteChange = React.useCallback(
    (exerciseAsset: T) => {
      if (!exerciseAsset) {
        return;
      }
      const sanitizedExercise = exerciseAsset[1];
      if (!sanitizedExercise) {
        return;
      }

      onSelectExercise?.(sanitizedExercise);
    },
    [exercise, onExistedChange],
  );

  const handleCustomizeExerciseButtonClick = React.useCallback(() => {
    if (matchedLibraryExercisesItem) {
      onSelectExercise?.(
        matchedLibraryExercisesItem as T,
        // team/public exercise asset tags are not customizable
        { ...exerciseAsset, tags: null } as T,
      );
    }

    setReadOnly(false);
  }, [
    matchedLibraryExercises,
    exerciseAsset,
    exercise,
    setReadOnly,
    onSelectExercise,
  ]);

  const showApplyValuesButton = React.useMemo(() => {
    return areExerciseValuesToApply(exercise, defaultComponentExerciseValues);
  }, []);

  const handleDefaultValuesButtonClick = React.useCallback(() => {
    setExercise(
      defaultSettingExerciseMode === CalloutStatus.SYNC
        ? {
            ...defaultNormalizedExercise,
            ...defaultComponentExerciseValues,
          }
        : defaultNormalizedExercise,
    );
    setDefaultSettingExerciseMode(
      defaultSettingExerciseMode === CalloutStatus.SYNC
        ? CalloutStatus.UNDO
        : CalloutStatus.SYNC,
    );
    setDirty(defaultSettingExerciseMode == CalloutStatus.SYNC);
  }, [
    defaultSettingExerciseMode,
    defaultNormalizedExercise,
    defaultComponentExerciseValues,
  ]);

  const handleArchiveRestoreExerciseSync = React.useCallback(() => {
    const privateExercise = (exerciseAsset as T) || activeExerciseAsset || null;
    handleExerciseFullInfoSync(
      privateArchivedCopyMode === CalloutStatus.SYNC
        ? (archivedCopy as T)
        : ({ ...privateExercise, name: archivedCopy?.name } as T),
    );

    setPrivateArchivedCopyMode(
      privateArchivedCopyMode === CalloutStatus.SYNC
        ? CalloutStatus.UNDO
        : CalloutStatus.SYNC,
    );
    setDefaultSettingExerciseMode(CalloutStatus.SYNC);
    setDirty(privateArchivedCopyMode == CalloutStatus.SYNC);
  }, [
    archivedCopy,
    exerciseAsset,
    activeExerciseAsset,
    onSelectExercise,
    privateArchivedCopyMode,
    defaultComponentExerciseValues,
    normalizeExercise,
    defaultValueContent,
  ]);

  // Syncing public/team exercise full info to the private exercise
  const handleApplyPublicTeamSync = React.useCallback(() => {
    const isSyncMode = publicTeamCopySyncMode === CalloutStatus.SYNC;
    handleExerciseFullInfoSync(
      isSyncMode ? (exerciseToSyncWith as T) : (activeExerciseAsset as T),
      isSyncMode ? false : true,
    );
    setPublicTeamCopySyncMode(
      publicTeamCopySyncMode === CalloutStatus.SYNC
        ? CalloutStatus.UNDO
        : CalloutStatus.SYNC,
    );
    setDefaultSettingExerciseMode(CalloutStatus.SYNC);
    setDirty(publicTeamCopySyncMode == CalloutStatus.SYNC);
  }, [
    activeExerciseAsset,
    handleExerciseFullInfoSync,
    exerciseToSyncWith,
    publicTeamCopySyncMode,
  ]);

  const actionLabel = readOnly
    ? "View"
    : exerciseAsset
      ? exerciseAsset?.isPendingApproval
        ? "Approve"
        : "Update"
      : "Create";

  const mainButtonLabel = exerciseAsset?.isPendingApproval
    ? "Approve"
    : isArchivedExercise
      ? "Restore"
      : createNewAsset
        ? "Create"
        : "Update" + " exercise";
  const handleCloseRenameMode = React.useCallback(() => {
    setRenameMode(false);
    setErrorMessage(null);
  }, []);

  const handleOpenRenameMode = React.useCallback(() => {
    setRenameMode(true);
    setRenamedTitle(exercise.title);
    setErrorMessage(null);
  }, [exercise.title]);

  React.useEffect(() => {
    if (!renameMode && delayedQuery?.length > 0) {
      refetchExerciseAssets();
    }
  }, [delayedQuery, renameMode]);

  const handleRenameExerciseClick = async () => {
    if (exerciseAsset?.name.trim() !== renamedTitle.trim()) {
      const { data: fetchedData } = await refetchExerciseAssets();

      const matchedOption = fetchedData?.items?.find(
        (option) => option.name.trim() === renamedTitle.trim(),
      );

      if (matchedOption) {
        setExistedExerciseWithSameName(matchedOption as T);
        setDirty(true);
        return;
      }
    }

    setExercise({
      ...exercise,
      title: renamedTitle.trim(),
    });
    setDirty(true);
    setExistedExerciseWithSameName(null);
    handleCloseRenameMode();
  };

  const handleSetEmptyExercise = React.useCallback(() => {
    setExercise({
      ...exercise,
      id: generateId(),
      title: "",
    });
    onSelectExercise?.(null);
  }, [exercise, onSelectExercise, generateId]);

  const handleCategoryChange =
    <T extends string>(setter: (value: T[]) => void) =>
    (options: string[]) => {
      const selectedOptions = options.map(
        (option) => option.toUpperCase() as T,
      );
      setter(selectedOptions);
      setDirty(true);
    };

  const handleTagsChange = (options: ExerciseTagDto[]) => {
    setSelectedTags(options);
    setDirty(true);
  };

  return (
    <>
      <Box className={clsx(s.root, className)} {...other}>
        <Box>
          <Typography
            variant="h2"
            display="flex"
            alignItems="flex-end"
            gap={1.5}
          >
            Exercise Library
            <Typography
              variant="h5"
              component="span"
              className={s.upsertExerciseLibraryText}
            >
              {actionLabel}
            </Typography>
          </Typography>

          <Typography variant="h6" className={s.label}>
            Exercise name
          </Typography>
          <Box>
            {renameMode ? (
              <TextField
                {...inputProps}
                variant="outlined"
                placeholder="Input new exercise name"
                className={s.textField}
                value={renamedTitle}
                onChange={handleRenamedTitleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="medium" onClick={handleCloseRenameMode}>
                        <ClearIcon
                          sx={{
                            color: (theme) => theme.palette.error.main,
                            fontSize: 18,
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="medium"
                        onClick={handleRenameExerciseClick}
                      >
                        <DoneIcon
                          sx={{
                            color: (theme) => theme.palette.success.main,
                            fontSize: 18,
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <ExerciseLibraryAutocomplete
                renderCustomInput={(
                  params: AutocompleteRenderInputParams,
                  isLoading,
                ) => (
                  <TextField
                    {...params}
                    {...inputProps}
                    variant="outlined"
                    className={clsx(s.textField, {
                      [s.pendingNoInput]: exerciseAsset?.isPendingApproval,
                    })}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        className: s.input,
                        endAdornment: isLoading ? (
                          <CircularProgress
                            size={18}
                            sx={{
                              color: (theme) => theme.palette.text.primary,
                            }}
                          />
                        ) : (
                          exerciseAsset &&
                          !exerciseAsset?.isPendingApproval &&
                          !(viewPublicOrTeamAsCoach || readOnly) && (
                            <MinimizedTooltip
                              title="Rename exercise"
                              isExerciseLibrary
                            >
                              <IconButton
                                size="medium"
                                onClick={handleOpenRenameMode}
                              >
                                <DriveFileRenameOutlineIcon
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.primary.main,
                                  }}
                                />
                              </IconButton>
                            </MinimizedTooltip>
                          )
                        ),
                      },
                    }}
                  />
                )}
                fullWidth
                freeSolo
                multiple
                inputValue={!exerciseAsset ? exercise.title : ""}
                value={
                  [exercise?.title as any].filter((e) => e.title !== "") as any
                }
                onInputChange={handleAutocompleteTitleChange}
                onCloseAutocomplete={handleCloseAutocomplete}
                isOptionSelected={!!exerciseAsset}
                onDisableSave={handleDisableSaveButton}
                disabled={!!exerciseAsset}
                onChange={handleAutocompleteChange}
                exercisesType={editingType as unknown as ExerciseAssetType}
                limitTags={1}
                renderTags={(record: any, _1) => {
                  return exerciseAsset ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={"space-between"}
                      sx={{ width: "85%" }}
                    >
                      <AddedLozenge
                        sx={{
                          maxWidth: "100%",
                          overflow: "hidden",
                          "& span": {
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            display: "block",
                          },
                        }}
                        text={exercise?.title}
                        disabled={
                          (readOnly && !viewPublicOrTeamAsCoach) ||
                          disableChangeExercise
                        }
                        onRemoveClick={handleSetEmptyExercise}
                      />
                      {exerciseAsset?.isPendingApproval &&
                        exerciseAsset?.pendingAssetClientInfDto?.clientId && (
                          <PendingAvatarWithTooltip
                            pendingAssetClientInfo={
                              exerciseAsset.pendingAssetClientInfDto
                            }
                          />
                        )}
                    </Box>
                  ) : null;
                }}
              />
            )}
            {!!existedExerciseWithSameName && (
              <Collapse in={!readOnly && renameMode} sx={{ my: 2 }}>
                <Box className={calloutStyles.container}>
                  <Typography className={calloutStyles.labelTip}>
                    <strong>NOTE:</strong> The library already contains an
                    active or archived exercise with this title. Please use a
                    unique name.
                    <br />
                    {!!allowChangeExerciseDuringRename &&
                      !isArchivedExercise && (
                        <Box
                          component="span"
                          sx={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            onSelectExercise(existedExerciseWithSameName);
                          }}
                        >
                          Move to an existing exercise
                        </Box>
                      )}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </Box>

          {!(
            (viewPublicOrTeamAsCoach && readOnly) ||
            isCurrentUserExerciseAsset
          ) &&
            !isArchivedExercise &&
            !renameMode && (
              <Collapse in={!!archivedCopy} sx={{ my: 2 }}>
                <DefaultSettingsCallout
                  text={
                    <>
                      <strong>PRO TIP:</strong> Apply your latest setting from
                      the archived exercise.
                    </>
                  }
                  status={privateArchivedCopyMode}
                  onClick={handleArchiveRestoreExerciseSync}
                />
              </Collapse>
            )}

          {!!exerciseToSyncWith &&
            activeExerciseAsset &&
            !renameMode &&
            !readOnly && (
              <Collapse in={!!activeExerciseAsset} sx={{ my: 2 }}>
                <DefaultSettingsCallout
                  text={
                    <>
                      <strong>PRO TIP:</strong> You already have private
                      exercise settings. Apply settings from the shared copy of
                      this exercise.
                    </>
                  }
                  status={publicTeamCopySyncMode}
                  onClick={handleApplyPublicTeamSync}
                />
              </Collapse>
            )}

          {((readOnly && exercise.video) || !readOnly) && (
            <>
              <Typography variant="h6" className={clsx(s.label, s.videoLabel)}>
                Video demo
              </Typography>

              <WorkoutDemoVideo
                onUpload={setAddOwnVideo}
                video={exercise.video}
                onRemoveVideo={handleRemoveDemoVideo}
                handleOpenModal={handleOpenModal}
                readonly={readOnly}
                onAddingVideoOrMedia={setIsAddingVideoOrMedia}
              />
            </>
          )}

          <Box ref={bodypartsBoxRef}>
            <CategorySelect
              label={CategoryTypeLabel.Bodypart}
              required={true}
              classes={{ label: s.categoryTagLabel }}
              selectedOptions={selectedBodypart}
              onChange={handleCategoryChange<BodypartType>(setSelectedBodypart)}
              disabled={readOnly}
              validateEmptySelection={true}
              description="Select at least one bodypart."
              highlightEmpty={highlightEmptyBodyparts}
              onChangeHighlightEmpty={setHighlightEmptyBodyparts}
            />
          </Box>
          <CategorySelect
            label={CategoryTypeLabel.MovementPattern}
            classes={{ label: s.categoryTagLabel }}
            selectedOptions={selectedMovementPatternType}
            onChange={handleCategoryChange<MovementPatternType>(
              setSelectedMovementPatternType,
            )}
            disabled={readOnly}
          />
          {/* TODO_exercise_library_tags: Remove condition in case of using tags for public exercises */}
          {(isCurrentUserExerciseAsset ||
            (editingType == ExerciseEditingType.PRIVATE && createNewAsset) ||
            editingType == ExerciseEditingType.ALL) &&
            (!viewPublicOrTeamAsCoach ||
              (viewPublicOrTeamAsCoach && !readOnly)) &&
            !isClient && (
              <ExerciseTagSelect
                label="Tags"
                classes={{ label: s.categoryTagLabel }}
                selectedOptions={selectedTags}
                onChange={handleTagsChange}
                disabled={readOnly}
              />
            )}

          {(readOnly && exercise?.images?.length) || !readOnly ? (
            <Typography variant="h6" className={s.label}>
              Media
            </Typography>
          ) : null}

          <Box className={s.images}>
            {!!exercise?.images?.length &&
              exercise.images.map((image, index) => (
                <WorkoutMedia
                  key={index}
                  image={image}
                  onRemove={handleRemoveMedia}
                  onToggleCover={handleToggleCoverMedia}
                  handleOpenModal={handleOpenModal}
                  modal={readOnly}
                />
              ))}
            {!readOnly && (
              <WorkoutUploadMedia
                onUpload={setAddMedia}
                onAddingVideoOrMedia={setIsAddingVideoOrMedia}
              />
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ my: 2 }}>
            <Typography variant="h6" fontSize={18}>
              {isClient ? "Example data" : "Default settings for programming"}
            </Typography>
            {!isClient ? (
              <Typography variant="body1" className={s.defaultsDescription}>
                Starting point for programming. Adapt default settings for a
                particular client and training day in the program.
              </Typography>
            ) : (
              <Typography variant="body1" className={s.defaultsDescription}>
                These values can differ for each exercise in your program.
              </Typography>
            )}
          </Box>
          {showApplyValuesButton && (
            <Collapse in={defaultSettingExerciseMode !== null}>
              <DefaultSettingsCallout
                text={
                  <>
                    <strong>PRO TIP:</strong> Apply your latest workout setting
                    from client program to Exercise Library.
                  </>
                }
                status={defaultSettingExerciseMode}
                onClick={handleDefaultValuesButtonClick}
              />
            </Collapse>
          )}
          <Typography variant="h6" className={s.label}>
            Instructions
          </Typography>
          <TextField
            className={s.textField}
            name="instructions"
            value={exercise.instructions}
            onChange={handleTextFieldChange}
            placeholder={!readOnly && "ex. Make sure to brace your core"}
            variant="outlined"
            fullWidth
            rows={5}
            multiline
            disabled={readOnly}
            slotProps={{
              input: {
                className: s.input,
              },
            }}
          />
          <>
            <div className={s.setsTopContent}>
              <Box className={s.setsContainerInput}>
                <Typography variant="h6" className={clsx(s.label, s.setsLabel)}>
                  Sets
                </Typography>
                <InputPlusMinus
                  value={exercise.sets?.length}
                  onClickMinus={handleSetsNumberMinus}
                  onClickPlus={handleSetsNumberPlus}
                  disabledMin={exercise.sets.length <= 1 || readOnly}
                  disabledMax={
                    exercise.sets.length >= MAX_EXERCISE_SETS_COUNT || readOnly
                  }
                />
              </Box>
              {!readOnly && (
                <Box>
                  <Button
                    className={s.settingsButton}
                    startIcon={<Settings fill={theme.palette.primary.main} />}
                    children="Settings"
                    onClick={handleClickSettings}
                  />
                </Box>
              )}
            </div>
            <Collapse in={openExerciseSettings}>
              <WorkoutExerciseSettings
                extraMeasurement={
                  exercise.typeExtraMeasurement || ExerciseTypeExtra.NONE
                }
                typeSet={exercise.typeSet || ExerciseTypeSet.WEIGHT}
                typeReps={exercise.typeReps || ExerciseTypeReps.WHOLE}
                units={exercise?.units || user.units}
                onChangeSettings={handleExerciseSettingsChange}
              />
            </Collapse>
            <CoachSets
              sets={exercise.sets}
              onChangeSet={handleSetFieldChange}
              units={exercise?.units || user.units}
              typeExtraMeasurement={
                exercise.typeExtraMeasurement || ExerciseTypeExtra.NONE
              }
              typeSet={exercise.typeSet || ExerciseTypeSet.WEIGHT}
              typeReps={exercise.typeReps || ExerciseTypeReps.WHOLE}
              disabled={readOnly}
              className={s.setsContainer}
            />
          </>
        </Box>
        {viewPublicOrTeamAsCoach && readOnly && (
          <Box className={s.actions}>
            <Button
              variant="contained"
              size="small"
              className={clsx(s.button, s.saveButton)}
              onClick={handleCustomizeExerciseButtonClick}
              fullWidth
            >
              Customize exercise
            </Button>
          </Box>
        )}
        {!readOnly && (
          <Box className={s.actions}>
            <MinimizedTooltip
              title={!dirty && "There are no changes to discard"}
              isExerciseActionButton
              isExerciseLibrary
              opacity={0.7}
            >
              <Button
                size="small"
                variant="outlined"
                className={clsx(s.button, s.cancelButton)}
                disabled={!dirty}
                onClick={handleDiscard}
                fullWidth
              >
                Discard
              </Button>
            </MinimizedTooltip>
            <Button
              variant="contained"
              size="small"
              className={clsx(s.button, s.saveButton)}
              fullWidth
              onClick={handleSave}
              disabled={
                !exercise.title ||
                isAddingVideoOrMedia ||
                disableSaveButton ||
                (exerciseAsset?.id && exerciseAsset?.id < 0) ||
                isFetching ||
                renameMode ||
                isPending
              }
            >
              {mainButtonLabel}
            </Button>
          </Box>
        )}
        <WorkoutActionsDelete
          title={"Delete item"}
          onCancel={handleCloseModal}
          onConfirm={confirmAction}
          open={visibleDeleteModal}
          image={chooseMedia?.media}
        />
      </Box>
    </>
  );
}
